/**
 * Pasture State / Paddock Detail Model — grouped `detail.landUse` / `detail.state` resolver.
 * See devdocs/features/pasture-forecasting/sources/pasture-state/Pasture-State-Paddock-Detail-Model-SoW.md (§5-§9, Appendix A) and
 * devdocs/features/pasture-forecasting/sources/pasture-state/Implementation-Plan-paddocks-v2.md (M3).
 *
 * This module is additive only: it reads the new grouped keys when present and falls back to
 * legacy `detail.pastureState` / `detail.pastureType` otherwise (SoW §8.1). It does not write
 * anything and is independent of `helper-functions/paddocks/pasture-state.js`, whose
 * `LEGACY_PASTURE_STATE_MAP` maps legacy `Resting` -> `Non-Agricultural` for record-history
 * normalisation — that mapping is the Non-Agricultural indexing defect this SoW exists to fix
 * (SoW §10 Phase 2), so it must not be reused here.
 */

import { differenceInCalendarDays } from 'date-fns'

export const PADDOCK_LAND_USE_TYPES = ['nonAgricultural', 'livestock', 'cropping']

export const PADDOCK_STATE_VALUES = ['nonAgricultural', 'resting', 'livestock', 'cropping', 'fallow']

// Legacy `detail.pastureState` -> `detail.landUse.type` fallback mapping, per SoW §9.1.
// Hay/blank/unknown resolve conservatively to livestock; they must never become nonAgricultural.
export const LEGACY_PASTURE_STATE_TO_LAND_USE = {
    'Non-Agricultural': 'nonAgricultural',
    Livestock: 'livestock',
    Cropping: 'cropping',
    Grazing: 'livestock',
    Resting: 'livestock',
    Hay: 'livestock',
}

const NORMALIZED_LEGACY_PASTURE_STATE_TO_LAND_USE = Object.fromEntries(Object.entries(LEGACY_PASTURE_STATE_TO_LAND_USE).map(([key, value]) => [key.toLowerCase(), value]))

export function legacyPastureStateToLandUse(pastureState) {
    const normalized = typeof pastureState === 'string' ? pastureState.trim().toLowerCase() : ''
    return NORMALIZED_LEGACY_PASTURE_STATE_TO_LAND_USE[normalized] ?? 'livestock'
}

export function defaultPaddockStateForLandUse(landUseType) {
    if (landUseType === 'nonAgricultural') return 'nonAgricultural'
    if (landUseType === 'cropping') return 'fallow'
    return 'resting'
}

export function mapLegacyPastureState(pastureState) {
    const landUseType = legacyPastureStateToLandUse(pastureState)
    return {
        landUseType,
        stateCurrent: defaultPaddockStateForLandUse(landUseType),
    }
}

/**
 * Resolves `detail.landUse.type`, falling back to legacy `detail.pastureState` (SoW §8.1, §9.1).
 * Blank/unknown/unrecognised legacy values fall back to `livestock`, never `nonAgricultural`.
 *
 * Prefers `detail.pastureStateRaw` over `detail.pastureState` when both are present: the Redux
 * normalisation layer (api/paddocks.js `normalizePaddocks()`) overwrites `pastureState` in place
 * via `migratePastureState()`, which itself maps legacy `Resting` -> `Non-Agricultural` — the
 * exact indexing defect this SoW exists to fix. `pastureStateRaw` is the untouched pre-migration
 * value, added specifically so this resolver isn't fed already-corrupted data.
 */
export function getLandUse(paddock) {
    const detail = paddock?.detail
    if (detail?.landUse?.type && PADDOCK_LAND_USE_TYPES.includes(detail.landUse.type)) {
        return detail.landUse.type
    }
    const legacyValue = detail?.pastureStateRaw ?? detail?.pastureState
    return legacyPastureStateToLandUse(legacyValue)
}

/**
 * Resolves `detail.cropping.cropType`, falling back to legacy `detail.pastureType` (SoW §8.1, §9.2).
 */
export function getCropType(paddock) {
    const detail = paddock?.detail
    if (detail?.cropping?.cropType !== undefined && detail.cropping.cropType !== null && detail.cropping.cropType !== '') return detail.cropping.cropType
    return getLandUse(paddock) === 'cropping' ? (detail?.pastureType ?? null) : null
}

// Grazing/cropping/treatments keys that `paddock-registry.js` groups under `detail.<group>.<key>`,
// each with a `legacyPath` back to the old flat `detail.<key>` (see paddock-registry.js). Once a
// paddock is saved through the gallery modal, `projectEntityToCanonical` (SoW §8.2) migrates these
// into the grouped path and strips the flat one — so any reader still using flat-only `detail.*`
// silently goes stale for migrated paddocks. `cropType` is deliberately excluded — use getCropType().
export const GROUPED_DETAIL_KEY_TO_GROUP = {
    dseLoad: 'grazing',
    stockingRate: 'grazing',
    foo: 'grazing',
    daysEmpty: 'grazing',
    grazingDaysRemaining: 'grazing',
    // Runtime-injected grazing defaults (SoW §3.2 — not yet user-configurable/persisted source
    // data) — grouped here too so the paddock detail page's Grazing tab reads them consistently
    // with everything else, even though they're display-only for now.
    defaultEnergyDensity: 'grazing',
    defaultResidual: 'grazing',
    dailyGrowthRate: 'grazing',
    targetStay: 'grazing',
    lastGrazedDate: 'grazing',
    previousGrazedDate: 'grazing',
    pastureGrowthRate: 'grazing',
    sowDate: 'cropping',
    harvestDate: 'cropping',
    cropTreatment: 'cropping',
    treatmentDate: 'cropping',
    chemicalUsed: 'treatments',
    paddockTreatment: 'treatments',
    nextTreatmentDueDate: 'treatments',
    dateLastPaddockTreatment: 'treatments',
}

/**
 * Resolves `detail.<group>.<key>` for a known grazing/cropping/treatments key, falling back to
 * the legacy flat `detail.<key>` (SoW §8.1). Mirrors getCropType's grouped-first shape.
 */
export function getGroupedDetailValue(paddock, key) {
    const detail = paddock?.detail
    const group = GROUPED_DETAIL_KEY_TO_GROUP[key]
    if (group) {
        const grouped = detail?.[group]?.[key]
        if (grouped !== undefined && grouped !== null && grouped !== '') return grouped
    }
    return detail?.[key] ?? null
}

// Which detail fields are relevant to *show* for a given current state — contextual, not
// one-size-fits-all (the whole point of grouping detail.grazing/cropping/treatments per the SoW).
// A resting paddock's DSE Load is stale/meaningless; a cropping paddock's grazing days remaining
// doesn't apply. cropType is deliberately not here — it's gated on landUse (static), not state.
const STATE_CONTEXT_FIELDS = {
    livestock: [{ key: 'dseLoad', t: '2292' }],
    resting: [
        { key: 'grazingDaysRemaining', t: '2847' },
        { key: 'daysEmpty', t: '2834' },
    ],
    cropping: [{ key: 'sowDate', t: '2836' }],
    fallow: [{ key: 'harvestDate', t: '2835' }],
}

/**
 * Fields relevant to a paddock's *current resolved state* (SoW: contextual display, not every
 * grazing/cropping field on every tile). Takes `state` rather than recomputing it — callers
 * already have it from resolvePaddockState().
 */
export function getStateContextFields(paddock, state) {
    const fields = STATE_CONTEXT_FIELDS[state] || []
    return fields.map(({ key, t }) => ({ key, t, value: getGroupedDetailValue(paddock, key) })).filter(f => f.value !== null && f.value !== undefined && f.value !== '')
}

/** Whole calendar days between `dateStr` and today, or null if missing/invalid. Never negative. */
function daysSince(dateStr) {
    if (!dateStr) return null
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return null
    return Math.max(0, differenceInCalendarDays(new Date(), date))
}

/**
 * Corner-badge content for a paddock tile — contextual per current state (SoW: different states
 * need different at-a-glance info, not one fixed metric for every paddock).
 *
 * `resting` deliberately uses the stored `grazing.daysEmpty` rather than computing days-since
 * `grazing.lastGrazedDate`: that field is a runtime-injected default (falls back to a hardcoded
 * date, see fmd-entities/defaults/paddock.js), not reliable per-paddock source data (SoW §3.2) —
 * computing a day-count from it would show a fabricated number for most paddocks. `sowDate`/
 * `harvestDate` are genuine conditional API-supplied fields (SoW §3.3), so real day-math on those
 * is safe.
 *
 * Falls back to area when the state's primary metric is missing — never blank.
 */
export function getStateBadge(paddock, state, opts = {}) {
    try {
        const { animalLoad, areaHa } = opts || {}
        const areaLabel = Number(areaHa) > 0 ? `${Number(areaHa).toFixed(1)} ha` : null

        if (state === 'livestock') {
            if (Number(animalLoad) > 0) return { label: `${animalLoad} hd` }
        } else if (state === 'resting') {
            const daysEmpty = getGroupedDetailValue(paddock, 'daysEmpty')
            if (daysEmpty !== null && daysEmpty !== undefined && daysEmpty !== '') return { label: `${daysEmpty}d` }
        } else if (state === 'cropping') {
            const days = daysSince(getGroupedDetailValue(paddock, 'sowDate'))
            if (days !== null) return { label: `Day ${days + 1}` }
        } else if (state === 'fallow') {
            const days = daysSince(getGroupedDetailValue(paddock, 'harvestDate'))
            if (days !== null) return { label: `${days}d` }
        }

        return areaLabel ? { label: areaLabel } : null
    } catch (err) {
        console.error('[getStateBadge]', err)
        return null
    }
}

// Assumed rest/fallow window when there's no reliable target — matches the paddock detail page
// mockup's own "~30 days" (explicitly approximate, marked with a tilde in the design), not a real
// registry field. There is no field anywhere for "expected days fallow"/"expected days empty".
export const ASSUMED_REST_WINDOW_DAYS = 30

function clampFraction(n) {
    if (!Number.isFinite(n) || n < 0) return 0
    return Math.min(1, n)
}

/**
 * `{count} {breed} {gender} grazing here` when every animal on the paddock is one cohort with both
 * fields set (matches the design mock's "42 Angus steers") — `breed`/`gender` live under
 * `detail` on livestock2 cohorts (with top-level fallback for any leftover callers), so this
 * degrades to a generic head count rather than assuming the specific fields exist.
 */
function livestockProgress(paddock, cohorts) {
    const list = (Array.isArray(cohorts) ? cohorts : []).filter(c => (Number(c?.size ?? c?.animalCount) || 0) > 0)
    const totalCount = list.reduce((sum, c) => sum + Number(c?.size ?? c?.animalCount), 0)
    if (totalCount <= 0) return null

    const breed = list[0]?.detail?.breed ?? list[0]?.breed
    const gender = list[0]?.detail?.gender ?? list[0]?.gender
    const headline = list.length === 1 && breed && gender ? `${totalCount} ${breed} ${String(gender).toLowerCase()} grazing here` : `${totalCount} head grazing here`

    // Earliest "placed on this paddock" date across the current cohort(s) when present —
    // livestock2 cohorts typically lack `detail.paddockDate`, so stay-progress no-ops.
    const placedDates = list
        .map(c => c?.detail?.paddockDate)
        .filter(Boolean)
        .map(d => new Date(d))
        .filter(d => !Number.isNaN(d.getTime()))
    const earliestPlaced = placedDates.length ? new Date(Math.min(...placedDates.map(d => d.getTime()))) : null
    const daysIn = earliestPlaced ? Math.max(0, differenceInCalendarDays(new Date(), earliestPlaced)) + 1 : null
    const targetStay = Number(getGroupedDetailValue(paddock, 'targetStay'))

    if (daysIn != null && Number.isFinite(targetStay) && targetStay > 0) {
        return { headline, fraction: clampFraction(daysIn / targetStay), caption: `Day ${daysIn} of a ${targetStay}-day stay` }
    }
    return { headline, fraction: null, caption: null }
}

function croppingProgress(paddock) {
    const cropType = getCropType(paddock)
    const sowDate = getGroupedDetailValue(paddock, 'sowDate')
    const harvestDate = getGroupedDetailValue(paddock, 'harvestDate')
    const headline = `${cropType || 'Crop'} growing here`

    const daysSown = daysSince(sowDate)
    if (daysSown == null) return { headline, fraction: null, caption: null }
    const dayN = daysSown + 1

    const sow = new Date(sowDate)
    const harvest = harvestDate ? new Date(harvestDate) : null
    const totalSpanDays = harvest && !Number.isNaN(harvest.getTime()) ? Math.max(1, differenceInCalendarDays(harvest, sow)) : null

    if (totalSpanDays != null) {
        return { headline, fraction: clampFraction(dayN / totalSpanDays), caption: `Day ${dayN} of ${totalSpanDays} days to harvest` }
    }
    return { headline, fraction: null, caption: `Day ${dayN}` }
}

function fallowProgress(paddock) {
    const daysSinceHarvest = daysSince(getGroupedDetailValue(paddock, 'harvestDate'))
    if (daysSinceHarvest == null) return { headline: 'Resting since harvest', fraction: null, caption: null }
    return {
        headline: `Resting since harvest - ${daysSinceHarvest} days ago`,
        fraction: clampFraction(daysSinceHarvest / ASSUMED_REST_WINDOW_DAYS),
        caption: `${daysSinceHarvest} of ~${ASSUMED_REST_WINDOW_DAYS} days fallow - next sow window approaching`,
    }
}

// Resting and Non-Agricultural have no design-mock example — per direction, both reuse Fallow's
// banner *shape* for now, substituted with `daysEmpty` (the closest real per-paddock signal to
// "days since harvest") since neither state has a harvest event of its own.
function restingProgress(paddock) {
    const daysEmptyRaw = getGroupedDetailValue(paddock, 'daysEmpty')
    const daysEmpty = Number(daysEmptyRaw)
    if (daysEmptyRaw === null || daysEmptyRaw === undefined || daysEmptyRaw === '' || !Number.isFinite(daysEmpty)) {
        return { headline: 'Resting', fraction: null, caption: null }
    }
    return {
        headline: `Resting - ${daysEmpty} days empty`,
        fraction: clampFraction(daysEmpty / ASSUMED_REST_WINDOW_DAYS),
        caption: `${daysEmpty} of ~${ASSUMED_REST_WINDOW_DAYS} days empty - next graze window approaching`,
    }
}

/**
 * Status-banner content for the paddock detail page's hero panel — `{ headline, fraction, caption
 * } | null`. `fraction` (0-1, or null when there's nothing to show progress toward) drives the
 * progress bar; `caption` is the small text under it. Contextual per state, same principle as
 * getStateBadge/getStateContextFields — never one fixed banner for every paddock.
 */
export function getStateProgress(paddock, state, opts = {}) {
    try {
        const { cohorts, mobs } = opts || {}
        if (state === 'livestock') return livestockProgress(paddock, cohorts ?? mobs)
        if (state === 'cropping') return croppingProgress(paddock)
        if (state === 'fallow') return fallowProgress(paddock)
        if (state === 'resting' || state === 'nonAgricultural') return restingProgress(paddock)
        return null
    } catch (err) {
        console.error('[getStateProgress]', err)
        return null
    }
}

/**
 * Derives the current visible/indexed paddock state (SoW §10 Phase 3, Appendix A).
 * `hasLivestockAssigned`/`hasActiveCropCycle` are injected so this stays a pure function —
 * callers wire them to whatever animal/cohort and crop-cycle data they have available.
 *
 * `hasActiveCropCycle` defaults to always-false: existing `sowDate`/`harvestDate` data isn't yet
 * confirmed reliable enough to drive state (SoW §13.8, §14.8 least-risk assumption), so cropping-
 * focused paddocks resolve to `fallow` until that's confirmed and wired up (Phase 3).
 */
export function resolvePaddockState(paddock, { hasLivestockAssigned, hasActiveCropCycle } = {}) {
    const landUse = getLandUse(paddock)
    const detail = paddock?.detail

    if (landUse === 'nonAgricultural') return 'nonAgricultural'
    if (hasLivestockAssigned?.(paddock)) return 'livestock'
    // Manual override of the base state (SoW §13.1 exception — see Appendix C 2026-07-23):
    // a user-authored detail.state.current with source 'manual' replaces the derived
    // fallow/cropping/resting base. Livestock presence still wins (above); nonAgricultural
    // landUse is absolute (above). This lets a cropping paddock be manually shown as Cropping
    // before a reliable crop-cycle event model exists.
    if (detail?.state?.source === 'manual' && detail.state.current) return detail.state.current
    if (landUse === 'cropping' && hasActiveCropCycle?.(paddock)) return 'cropping'
    return defaultPaddockStateForLandUse(landUse)
}

// Display metadata for the 5-value model (SoW §6.2). Translation keys/colours reuse the
// existing entries already defined for these exact states in helper-functions/paddocks/general.js
// and helper-functions/paddocks/pasture-state.js, so no new i18n keys are needed.
export const PADDOCK_STATES = [
    { state: 'livestock', color: '#32CD32B3', faIcon: 'fad fa-sheep', t: '2637' },
    { state: 'resting', color: '#ADFF2FB3', faIcon: 'fad fa-seedling', t: '1834' },
    { state: 'cropping', color: '#FFD700B3', faIcon: 'fad fa-tractor', t: '2883' },
    { state: 'fallow', color: '#A9A9A9B3', faIcon: 'fad fa-knife-kitchen', t: '1851' },
    { state: 'nonAgricultural', color: '#A9A9A9B3', faIcon: 'fad fa-building', t: '5383' },
]

export function paddockStateToTranslationKey(state) {
    return PADDOCK_STATES.find(s => s.state === state)?.t ?? null
}

// Pill badge colors for the 5-value state model — shared by the paddock-tile-v2 card design
// (pad-db-tiles-v2.js) and the paddocks table's State column, so both read from one definition
// instead of two copies drifting apart. Distinct from PADDOCK_STATES.color above (that one is
// unused dead data today — its only consumer, paddock-dashboard.js's filter tabs, reads
// .faIcon/.t only) and from general.js's PASTURE_STATES (that one colors map polygons, a
// different visual context with different constraints — see pad-db-tiles-v2.js's comments).
export const PADDOCK_STATE_PILL_STYLE = {
    livestock: { background: '#dceedb', color: '#3c6b39' },
    resting: { background: '#f7e3c9', color: '#8a5a2b' },
    cropping: { background: '#f2edc4', color: '#7a712c' },
    fallow: { background: '#f0dcc9', color: '#8b4226' },
    nonAgricultural: { background: '#e4e4e4', color: '#5a5a5a' },
}

// helper-functions/paddocks/general.js's PASTURE_STATES keys non-agricultural as a lowercase,
// hyphenated string rather than this module's camelCase `nonAgricultural` — this bridges the two
// so paddocks-v2 can still use pastureStateToColor() for map/badge colouring.
export const PADDOCK_STATE_TO_GENERAL_STATE_KEY = {
    livestock: 'livestock',
    resting: 'resting',
    cropping: 'cropping',
    fallow: 'fallow',
    nonAgricultural: 'non-agricultural',
}
